import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bell, Inbox } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { StatusPill, type PillTone } from "@/components/ui/Callout";

/**
 * NotificationBell (#831 concept preview) — the notification panel pulled from
 * the UI-pitch concept, fitted to the current AppShell top bar. A bell → a
 * right-anchored dropdown of the user's recent digests (GET /api/digests, which
 * is already per-user scoped), rendered in the concept's tinted-row language.
 *
 * Preview-gated: mounted only when the concept-preview flag is on, so it never
 * changes what current users see until they opt in. No new backend — it reuses
 * the digest feed the 7am cron already writes.
 */
interface DigestRow {
  id: string;
  period: string;
  title: string;
  created_at: number;
}

const PERIOD_TONE: Record<string, PillTone> = {
  daily: "info",
  weekly: "good",
  monthly: "warn",
  annual: "neutral",
};

function relativeTime(epochMs: number): string {
  const diff = Date.now() - epochMs;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now"; // page-honesty-ok: computed from the timestamp above
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(epochMs).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ["notification-digests"],
    queryFn: () => apiFetch<DigestRow[]>("/api/digests?period=daily"),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  });

  const items = data ?? [];
  // "Unread" heuristic (v1, per-device): items newer than the last time the
  // panel was opened. Good enough to show the pattern without a backend read-state.
  const [lastSeen, setLastSeen] = useState<number>(() => {
    if (typeof window === "undefined") return 0;
    return Number(localStorage.getItem("crosbe-notif-seen") ?? 0);
  });
  const unread = items.filter((d) => d.created_at > lastSeen).length;

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && items.length) {
      const newest = Math.max(...items.map((d) => d.created_at));
      setLastSeen(newest);
      try {
        localStorage.setItem("crosbe-notif-seen", String(newest));
      } catch {
        /* best-effort */
      }
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={toggle}
        aria-label="Notifications"
        className="relative flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <p className="text-sm font-semibold">Notifications</p>
            <span className="text-xs text-muted-foreground">Daily digests</span>
          </div>

          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
              <Inbox className="h-6 w-6 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No recent digests</p>
              <p className="max-w-[16rem] text-xs text-muted-foreground/70">
                Plant digests land here each morning once the daily cron runs for
                your account.
              </p>
            </div>
          ) : (
            <ul className="max-h-96 overflow-y-auto py-1">
              {items.slice(0, 12).map((d) => (
                <li key={d.id}>
                  <a
                    href={`/updates`}
                    className="flex items-start gap-2.5 px-3 py-2.5 transition-colors hover:bg-accent"
                  >
                    <span className="mt-0.5">
                      <StatusPill tone={PERIOD_TONE[d.period] ?? "neutral"}>
                        {d.period}
                      </StatusPill>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-foreground">
                        {d.title}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {relativeTime(d.created_at)}
                      </span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
